import { Bell, ClipboardList, FileText, Flame, RefreshCw } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { Notification } from '@/api/notifications.api';

/**
 * Presentation helpers shared by the notifications dropdown panel and
 * the full-page list. Kept out of the `.tsx` so React Fast Refresh
 * treats the component files as component-only.
 */

/** How a notification `kind` renders in a row: a leading icon + a label. */
export interface NotificationKindPresentation {
  icon: LucideIcon;
  label: string;
}

/**
 * Icon + label per notification `kind`. New kinds should be added here; any
 * kind without an entry falls back to {@link DEFAULT_KIND_PRESENTATION}. Keep
 * in step with the server's notification kinds (e.g. `sop_updated` /
 * `sop_published` from ADR-038 SOP-update propagation).
 */
const KIND_PRESENTATION: Record<string, NotificationKindPresentation> = {
  task_assigned: { icon: ClipboardList, label: 'Task assigned' },
  sop_updated: { icon: RefreshCw, label: 'SOP updated' },
  sop_published: { icon: FileText, label: 'SOP published' },
  /** ADR-048 — a report hit a streak milestone; the lead is nudged to recognize it. */
  streak_milestone: { icon: Flame, label: 'Streak milestone' },
};

/** Fallback for any `kind` not in {@link KIND_PRESENTATION}. */
const DEFAULT_KIND_PRESENTATION: NotificationKindPresentation = { icon: Bell, label: 'Notification' };

/** Resolve the icon + label for a notification `kind`, with a safe default. */
export function notificationKindPresentation(kind: string): NotificationKindPresentation {
  return KIND_PRESENTATION[kind] ?? DEFAULT_KIND_PRESENTATION;
}

/** Unread first, then newest first — the inbox's reading order. */
export function sortUnreadFirst(items: Notification[]): Notification[] {
  return [...items].sort((a, b) => {
    const aUnread = a.readAt === null ? 0 : 1;
    const bUnread = b.readAt === null ? 0 : 1;
    if (aUnread !== bUnread) return aUnread - bUnread;
    return Date.parse(b.createdAt) - Date.parse(a.createdAt);
  });
}

/** "just now", "5m ago", "3h ago", "2d ago", or a short date past a week. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const diffMs = now - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${String(min)}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${String(hr)}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${String(day)}d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
